import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery } from "@mui/material";
import Loader from "../../../common/loader";
import { commonMessage } from "../../../constants/message.constant";
import { Customer, Deal } from "../../../validation/customer.validation";
import { getLoginUserData } from "../../../services/account.service";
import { getInactiveDeals } from "../../../services/customer.service";
import DealsMobileCardView from "../deals/DealsMobileCardView";
import DealsDesktopTableView from "../deals/DealsDesktopTableView";
import TermSheetView from "../deals/TermSheetView";

const PAGE_LIMIT = 25;

const InactiveDealsPage: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [orderBy, setOrderBy] = useState<keyof Deal>("loanName");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [loggedInUser, setLoggedInUser] = useState<Customer | null>(null);
  const [viewingDeal, setViewingDeal] = useState<Deal | null>(null);
  const [viewingBankerId, setViewingBankerId] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: PAGE_LIMIT,
    total: 0,
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  const loadDeals = async (search: string = searchQuery, page: number = paginationData.page) => {
    try {
      setLoading(true);
      const response = await getInactiveDeals(search, page, PAGE_LIMIT);
      setDeals(response?.data || []);
      setPaginationData({
        page: response?.page || page,
        limit: response?.limit || PAGE_LIMIT,
        total: response?.total || 0,
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || error?.response?.data?.message || commonMessage.unknownError,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals("", 1);
    const init = async () => {
      try {
        const user = await getLoginUserData();
        setLoggedInUser(user);
      } catch {
        console.error("Failed to load user data");
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) => {
      const aValue: unknown = a[orderBy as keyof Deal];
      const bValue: unknown = b[orderBy as keyof Deal];

      if (aValue === undefined || aValue === null) return order === "asc" ? 1 : -1;
      if (bValue === undefined || bValue === null) return order === "asc" ? -1 : 1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [deals, orderBy, order]);

  const handleSort = (columnId: keyof Deal) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadDeals(query, 1);
    }, 500);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    loadDeals(searchQuery, page);
  };

  const handleRowExpand = (dealId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(dealId)) newExpanded.delete(dealId);
    else newExpanded.add(dealId);
    setExpandedRows(newExpanded);
  };

  const noop = () => undefined;

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "center" },
          justifyContent: { xs: "center", md: "space-between" },
          gap: { xs: 2, md: 0 },
          mb: 6,
          p: 2,
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box>
          <Box
            sx={{
              fontWeight: 700,
              color: "#333",
              textAlign: { xs: "center", md: "left" },
              fontSize: 22,
            }}
          >
            Inactive Loan Deals
          </Box>
        </Box>

        <TextField
          label="Search Inactive Deals"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            minWidth: { xs: 200, sm: 250 },
            width: { xs: "100%", sm: "auto" },
          }}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : isMobile ? (
        <DealsMobileCardView
          deals={sortedDeals}
          loggedInUser={loggedInUser}
          expandedRows={expandedRows}
          onToggleExpand={handleRowExpand}
          onAssignClick={noop}
          onEditClick={noop}
          onDeleteClick={noop}
          onViewTermSheet={(deal, bankerId) => {
            setViewingDeal(deal);
            setViewingBankerId(bankerId);
          }}
          onClosingClick={noop}
          actionsDisabled
        />
      ) : (
        <DealsDesktopTableView
          deals={sortedDeals}
          loggedInUser={loggedInUser}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
          expandedRows={expandedRows}
          onToggleExpand={handleRowExpand}
          onAssignClick={noop}
          onEditClick={noop}
          onDeleteClick={noop}
          onViewTermSheet={(deal, bankerId) => {
            setViewingDeal(deal);
            setViewingBankerId(bankerId);
          }}
          onClosingClick={noop}
          actionsDisabled
        />
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
          <Pagination count={totalPages} page={paginationData.page} onChange={handlePageChange} color="primary" />
          <Typography variant="body2" color="text.secondary">
            Showing {(paginationData.page - 1) * paginationData.limit + 1} to{" "}
            {Math.min(paginationData.page * paginationData.limit, paginationData.total)} of{" "}
            {paginationData.total}
          </Typography>
        </Box>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false, message: "" })}
      >
        <Alert severity={snackbar.severity} elevation={6} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={!!viewingDeal}
        onClose={() => setViewingDeal(null)}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { overflow: "hidden", m: 0, p: 0 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          Term Sheet Details
          <IconButton
            aria-label="close"
            onClick={() => setViewingDeal(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ m: 0, p: 2 }}>
          {viewingDeal ? <TermSheetView deal={viewingDeal} bankerId={viewingBankerId} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InactiveDealsPage;
